import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import InfiniteScroll from 'react-infinite-scroll-component';
import { X, ChevronLeft, ChevronRight, MessageCircle } from 'lucide-react';
import Comments from './Comments';
import { host } from './script/variables';
import './style/usergallery.css';

// ─── Helpers ────────────────────────────────────────────────────────────────

function getToken() {
  return localStorage.getItem('token') ?? '';
}

function getStoredUser() {
  try {
    const raw = localStorage.getItem('cafeuser');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

const getUserLabel = (user) =>
  (user && (user.displayName || user.username)) || 'Anonymous';

function timeAgo(timestamp) {
  if (!timestamp) return '';
  const d = new Date(timestamp);
  if (isNaN(d)) return '';
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  if (diff < 2629800) return `${Math.floor(diff / 604800)}w ago`;
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

const PAGE_SIZE = 12;
const SWIPE_THRESHOLD = 50; // px

// ─── Thumbnail (grid item) ──────────────────────────────────────────────────

const GalleryThumb = ({ item, index, onOpen }) => {
  const [loaded, setLoaded] = useState(false);

  return (
    <button
      className="ug-thumb"
      onClick={() => onOpen(index)}
      aria-label={`Open photo ${index + 1}`}
    >
      {!loaded && <span className="ug-thumb-shimmer" aria-hidden="true" />}
      <img
        className={`ug-thumb-img ${loaded ? 'loaded' : ''}`}
        src={item.imageurl}
        alt={item.caption || 'gallery photo'}
        loading="lazy"
        onLoad={() => setLoaded(true)}
      />
    </button>
  );
};

// ─── Comments drawer ────────────────────────────────────────────────────────

const CommentsDrawer = ({ postId, isOpen, onClose, isLogin, commentCount, setCommentCount }) => {
  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  const handleBackdrop = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div
      className={`ug-drawer-backdrop ${isOpen ? 'open' : ''}`}
      onClick={handleBackdrop}
      aria-hidden={!isOpen}
    >
      <div className={`ug-drawer ${isOpen ? 'open' : ''}`} role="dialog" aria-label="Comments" aria-modal="true">
        <div className="ug-drawer-handle" />
        <div className="ug-drawer-header">
          <span className="ug-drawer-title">{commentCount} comments</span>
          <button className="ug-icon-btn" onClick={onClose} aria-label="Close comments">
            <X size={18} />
          </button>
        </div>
        <div className="ug-drawer-body">
          {isOpen && (
            <Comments
              postid={postId}
              commentCount={commentCount}
              setCommentCount={setCommentCount}
              isLogin={isLogin}
              isBlog={false}
              isMoment={true}
            />
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Full-screen viewer ─────────────────────────────────────────────────────

const GalleryViewer = ({ images, index, onClose, onNavigate, isLogin }) => {
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [commentCount, setCommentCount] = useState(0);
  const [imgLoaded, setImgLoaded] = useState(false);
  const touchStartX = useRef(null);

  const item = images[index];

  useEffect(() => {
    setImgLoaded(false);
    setCommentCount(item?.commentCount ?? 0);
  }, [index, item]);

  // preload neighbours for snappy prev/next
  useEffect(() => {
    [index - 1, index + 1].forEach((i) => {
      const neighbour = images[i];
      if (neighbour?.imageurl) {
        const img = new Image();
        img.src = neighbour.imageurl;
      }
    });
  }, [index, images]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') onNavigate(1);
      if (e.key === 'ArrowLeft') onNavigate(-1);
    };
    window.addEventListener('keydown', handleKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKey);
    };
  }, [onClose, onNavigate]);

  const handleTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const handleTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (delta > SWIPE_THRESHOLD) onNavigate(-1);
    else if (delta < -SWIPE_THRESHOLD) onNavigate(1);
    touchStartX.current = null;
  };

  if (!item) return null;

  const userName = getUserLabel(item.user);
  const avatar = item.user?.image && item.user.image !== '#' ? item.user.image : null;

  return (
    <div className="ug-viewer" role="dialog" aria-modal="true" aria-label="Photo viewer">
      <div className="ug-viewer-header">
        <div className="ug-viewer-user">
          {avatar ? (
            <img className="ug-viewer-avatar" src={avatar} alt={userName} />
          ) : (
            <div className="ug-viewer-avatar ug-avatar-fallback">{userName[0]?.toUpperCase() ?? '?'}</div>
          )}
          <div className="ug-viewer-user-meta">
            <span className="ug-viewer-username">{userName}</span>
            <span className="ug-viewer-time">{timeAgo(item.createdAt)}</span>
          </div>
        </div>
        <button className="ug-icon-btn" onClick={onClose} aria-label="Close">
          <X size={22} />
        </button>
      </div>

      <div
        className="ug-viewer-stage"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <button
          className="ug-nav-btn ug-nav-prev"
          onClick={() => onNavigate(-1)}
          aria-label="Previous photo"
        >
          <ChevronLeft size={26} />
        </button>

        <div className="ug-viewer-img-wrap">
          {!imgLoaded && <span className="ug-viewer-spinner" aria-hidden="true" />}
          <img
            key={item._id ?? index}
            className={`ug-viewer-img ${imgLoaded ? 'loaded' : ''}`}
            src={item.imageurl}
            alt={item.caption || 'moment'}
            onLoad={() => setImgLoaded(true)}
          />
        </div>

        <button
          className="ug-nav-btn ug-nav-next"
          onClick={() => onNavigate(1)}
          aria-label="Next photo"
        >
          <ChevronRight size={26} />
        </button>
      </div>

      <div className="ug-viewer-footer">
        {item.caption && <p className="ug-viewer-caption">{item.caption}</p>}
        <button className="ug-comment-btn" onClick={() => setCommentsOpen(true)}>
          <MessageCircle size={18} />
          <span>{commentCount > 0 ? `${commentCount} comments` : 'Add a comment'}</span>
        </button>
      </div>

      <CommentsDrawer
        postId={item._id}
        isOpen={commentsOpen}
        onClose={() => setCommentsOpen(false)}
        isLogin={isLogin}
        commentCount={commentCount}
        setCommentCount={setCommentCount}
      />
    </div>
  );
};

// ─── Main component ─────────────────────────────────────────────────────────

const UserGallery = () => {
  const [params] = useSearchParams();
  const cafeId = params.get('cid');

  const [images, setImages] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true);
  const [error, setError] = useState(null);
  const [viewerIndex, setViewerIndex] = useState(null); // null = closed
  const [isLogin] = useState(() => !!(getStoredUser() && getToken()));

  const pageRef = useRef(1);
  const loadingRef = useRef(false);
  const abortRef = useRef(null);

  const fetchImages = useCallback(async () => {
    if (loadingRef.current || !cafeId) return;
    loadingRef.current = true;
    setError(null);

    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const res = await fetch(
        `${host}/masala/fetchusermedia?cid=${cafeId}&pno=${pageRef.current}&c=${PAGE_SIZE}`,
        { method: 'GET', headers: { 'Content-Type': 'application/json' }, signal: controller.signal }
      );
      if (!res.ok) throw new Error(`Server error ${res.status}`);
      const batch = await res.json();

      setImages((prev) => [...prev, ...batch]);
      setHasMore(batch.length >= PAGE_SIZE);
      pageRef.current += 1;
    } catch (err) {
      if (err.name !== 'AbortError') setError('Could not load photos. Please try again.');
    } finally {
      loadingRef.current = false;
      setInitialLoad(false);
    }
  }, [cafeId]);

  // Reset + fetch whenever the cafe changes
  useEffect(() => {
    setImages([]);
    setHasMore(true);
    setInitialLoad(true);
    pageRef.current = 1;
    fetchImages();
    return () => abortRef.current?.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cafeId]);

  const openViewer = useCallback((index) => setViewerIndex(index), []);
  const closeViewer = useCallback(() => setViewerIndex(null), []);

  const navigateViewer = useCallback((delta) => {
    setViewerIndex((cur) => {
      if (cur == null) return cur;
      const next = cur + delta;
      if (next < 0) return images.length - 1;
      if (next >= images.length) return 0;
      return next;
    });
  }, [images.length]);

  const showEmpty = !initialLoad && images.length === 0 && !error;

  return (
    <div className="ug-root">
      <div className="ug-header">
        <h2 className="ug-title">Gallery</h2>
        <span className="ug-subtitle">Moments shared at this place</span>
      </div>

      {initialLoad && (
        <div className="ug-grid">
          {Array.from({ length: 9 }).map((_, i) => (
            <div key={`sk-${i}`} className="ug-thumb ug-thumb-skeleton" aria-hidden="true">
              <span className="ug-thumb-shimmer" />
            </div>
          ))}
        </div>
      )}

      {!initialLoad && images.length > 0 && (
        <InfiniteScroll
          dataLength={images.length}
          next={fetchImages}
          hasMore={hasMore}
          loader={<div className="ug-loader"><span className="ug-spinner" /></div>}
          endMessage={<p className="ug-end-message">You've seen every moment here ✨</p>}
        >
          <div className="ug-grid">
            {images.map((item, index) => (
              <GalleryThumb
                key={item._id ?? index}
                item={item}
                index={index}
                onOpen={openViewer}
              />
            ))}
          </div>
        </InfiniteScroll>
      )}

      {showEmpty && (
        <div className="ug-empty">
          <span className="ug-empty-icon">📸</span>
          <p>No photos here yet</p>
          <p className="ug-empty-sub">Be the first to share a moment from this place</p>
        </div>
      )}

      {error && (
        <div className="ug-error">
          <span>{error}</span>
          <button onClick={fetchImages}>Retry</button>
        </div>
      )}

      {viewerIndex !== null && (
        <GalleryViewer
          images={images}
          index={viewerIndex}
          onClose={closeViewer}
          onNavigate={navigateViewer}
          isLogin={isLogin}
        />
      )}
    </div>
  );
};

export default UserGallery;